import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import { RsIcon } from '../icons'

export default function PlatformNoticeWatcher() {
  const { user, loading, isAdmin } = useAuth()
  const [queue, setQueue] = useState([])
  const [current, setCurrent] = useState(null)
  const [acknowledging, setAcknowledging] = useState(false)

  const loadPending = useCallback(async () => {
    if (!user || isAdmin) {
      setQueue([])
      setCurrent(null)
      return
    }
    try {
      const res = await axios.get('/api/notices/pending')
      const notices = res.data?.notices || []
      setQueue(notices)
      setCurrent(notices[0] || null)
    } catch {
      setQueue([])
      setCurrent(null)
    }
  }, [user, isAdmin])

  useEffect(() => {
    if (loading) return undefined
    loadPending()
    const onFocus = () => loadPending()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [loading, loadPending])

  const acknowledge = async () => {
    if (!current || acknowledging) return
    setAcknowledging(true)
    try {
      await axios.post(`/api/notices/${current.id}/acknowledge`)
      const remaining = queue.filter((n) => n.id !== current.id)
      setQueue(remaining)
      setCurrent(remaining[0] || null)
    } catch {
      /* keep modal open so user can retry */
    } finally {
      setAcknowledging(false)
    }
  }

  if (!user || isAdmin || !current || typeof document === 'undefined') return null

  return createPortal(
    <div className="platform-notice-overlay" role="presentation">
      <div
        className="platform-notice-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-notice-title"
      >
        <div className="platform-notice-icon" aria-hidden>
          <RsIcon name="bell" size="xl" />
        </div>
        <h2 id="platform-notice-title" className="platform-notice-title">{current.title}</h2>
        <div className="platform-notice-body">
          {String(current.body || '').split('\n').map((line, index) => (
            <React.Fragment key={`${index}-${line.slice(0, 12)}`}>
              {line}
              {index < String(current.body || '').split('\n').length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </div>
        {queue.length > 1 && (
          <p className="platform-notice-queue-hint">
            {queue.length - 1} more notice{queue.length - 1 === 1 ? '' : 's'} after this
          </p>
        )}
        <button
          type="button"
          className="platform-notice-btn"
          onClick={acknowledge}
          disabled={acknowledging}
        >
          {acknowledging ? 'Saving…' : "I've Read This / 我已知晓"}
        </button>
      </div>
    </div>,
    document.body
  )
}
